import React from 'react';
import { Eye, Users, Package, ShoppingBag, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { OrderStatusBadge } from './OrderStatusBadge';

const SummaryCard = ({ label, value, Icon, bg, border, labelColor, iconColor }) => (
  <Card className={`${bg} ${border}`}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className={`${labelColor} text-sm`}>{label}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
        </div>
        <Icon className={`w-10 h-10 ${iconColor} opacity-80`} />
      </div>
    </CardContent>
  </Card>
);

const TimeBreakdown = ({ title, data, accent }) => (
  <Card className={`bg-gradient-to-br ${accent.bg} ${accent.border}`}>
    <CardHeader className="pb-2">
      <CardTitle className={`${accent.title} text-lg flex items-center gap-2`}>
        <Eye className="w-5 h-5" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <div>
          <p className={`${accent.text} text-sm`}>{data?.current_label || 'Current'}</p>
          <p className="text-3xl font-bold text-white">{data?.current?.visits || 0}</p>
          <p className={`${accent.subtext} text-xs`}>
            {data?.current?.unique_visitors || 0} unique visitors
          </p>
        </div>
        <div className={`border-t ${accent.divider} pt-3`}>
          <p className={`${accent.text} text-sm`}>{data?.previous_label || 'Previous'}</p>
          <p className={`text-xl font-semibold ${accent.value}`}>{data?.previous?.visits || 0}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const AnalyticsTab = ({ analytics, orders }) => {
  const weekly = analytics?.time_breakdown?.weekly;
  const monthly = analytics?.time_breakdown?.monthly;
  const yearly = analytics?.time_breakdown?.yearly;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <SummaryCard
          label="Total Visits"
          value={analytics?.summary?.total_visits || 0}
          Icon={Eye}
          bg="bg-purple-800/50"
          border="border-purple-600"
          labelColor="text-purple-300"
          iconColor="text-yellow-400"
        />
        <SummaryCard
          label="Unique Visitors"
          value={analytics?.summary?.unique_visitors || 0}
          Icon={Users}
          bg="bg-purple-800/50"
          border="border-purple-600"
          labelColor="text-purple-300"
          iconColor="text-blue-400"
        />
        <SummaryCard
          label="Product Views"
          value={analytics?.summary?.product_views || 0}
          Icon={Package}
          bg="bg-purple-800/50"
          border="border-purple-600"
          labelColor="text-purple-300"
          iconColor="text-green-400"
        />
        <SummaryCard
          label="Orders"
          value={orders?.total_orders || 0}
          Icon={ShoppingBag}
          bg="bg-green-900/30"
          border="border-green-700"
          labelColor="text-green-300"
          iconColor="text-green-400"
        />
        <SummaryCard
          label="Revenue"
          value={`$${orders?.total_revenue?.toFixed(2) || '0.00'}`}
          Icon={DollarSign}
          bg="bg-yellow-900/30"
          border="border-yellow-700"
          labelColor="text-yellow-300"
          iconColor="text-yellow-400"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <TimeBreakdown
          title="Weekly Visits"
          data={{
            current_label: 'This Week',
            previous_label: 'Last Week',
            current: weekly?.this_week,
            previous: weekly?.last_week,
          }}
          accent={{
            bg: 'from-blue-900/50 to-blue-800/30',
            border: 'border-blue-600',
            title: 'text-blue-300',
            text: 'text-blue-200',
            subtext: 'text-blue-300',
            value: 'text-blue-100',
            divider: 'border-blue-700',
          }}
        />
        <TimeBreakdown
          title="Monthly Visits"
          data={{
            current_label: monthly?.this_month?.month || 'This Month',
            previous_label: monthly?.last_month?.month || 'Last Month',
            current: monthly?.this_month,
            previous: monthly?.last_month,
          }}
          accent={{
            bg: 'from-purple-900/50 to-purple-800/30',
            border: 'border-purple-500',
            title: 'text-purple-300',
            text: 'text-purple-200',
            subtext: 'text-purple-300',
            value: 'text-purple-100',
            divider: 'border-purple-700',
          }}
        />
        <TimeBreakdown
          title="Yearly Visits"
          data={{
            current_label: yearly?.this_year?.year || 'This Year',
            previous_label: yearly?.last_year?.year || 'Last Year',
            current: yearly?.this_year,
            previous: yearly?.last_year,
          }}
          accent={{
            bg: 'from-yellow-900/50 to-yellow-800/30',
            border: 'border-yellow-600',
            title: 'text-yellow-300',
            text: 'text-yellow-200',
            subtext: 'text-yellow-300',
            value: 'text-yellow-100',
            divider: 'border-yellow-700',
          }}
        />
      </div>

      <Card className="bg-gradient-to-r from-purple-900/60 to-yellow-900/40 border-purple-500 mb-8">
        <CardContent className="py-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-purple-200 text-sm">All-Time Visits</p>
              <p className="text-4xl font-bold text-white">
                {analytics?.time_breakdown?.all_time?.visits || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-purple-200 text-sm">All-Time Unique Visitors</p>
              <p className="text-4xl font-bold text-yellow-400">
                {analytics?.time_breakdown?.all_time?.unique_visitors || 0}
              </p>
            </div>
            <div className="text-center">
              <p className="text-purple-200 text-sm">Total Accounts</p>
              <p className="text-4xl font-bold text-green-400">
                {analytics?.summary?.total_accounts || 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="text-white">Top Viewed Products</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_products?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_products.map((product, index) => (
                  <div
                    key={`top-${product.product_id}-${index}`}
                    className="flex items-center justify-between p-3 bg-purple-900/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-400 font-bold">#{index + 1}</span>
                      <span className="text-white">{product.product_name}</span>
                    </div>
                    <span className="text-purple-300">{product.views} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-purple-300 text-center py-8">No product views yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-purple-800/50 border-purple-600">
          <CardHeader>
            <CardTitle className="text-white">Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {orders?.recent_orders?.length > 0 ? (
              <div className="space-y-3">
                {orders.recent_orders.slice(0, 5).map((order) => (
                  <div
                    key={`recent-${order.order_number}`}
                    className="flex items-center justify-between p-3 bg-purple-900/50 rounded-lg"
                  >
                    <div>
                      <p className="text-white font-medium">{order.order_number}</p>
                      <p className="text-purple-400 text-sm">{order.customer?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-yellow-400 font-bold">
                        ${order.pricing?.total?.toFixed(2)}
                      </p>
                      <OrderStatusBadge status={order.status} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-purple-300 text-center py-8">No orders yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
